const FLAT_SPREAD_THRESHOLD = 0.05;

const shortName = (name) =>
  String(name || '')
    .replace(/ School District$/, '')
    .replace(/ Planning Region$/, '');

/**
 * Which indicator is actually moving the ranking.
 *
 * Weight alone is misleading: an indicator can carry 15% and separate nobody
 * if every entity scores the same on it. Contribution spread (max - min of
 * score x weight) is what changes rank order, and it already accounts for
 * weight because a zero-weight indicator contributes zero everywhere.
 */
export function rankingDriver(rows, indicators) {
  if (!rows?.length || !indicators?.length) return null;

  const stats = indicators
    .map((indicator) => {
      const contributions = rows
        .map((row) => row.contributions.find((item) => item.id === indicator.id))
        .filter(Boolean);
      const scored = contributions.filter((item) => !item.missing);
      if (!scored.length) return null;

      let min = Infinity;
      let max = -Infinity;
      let sum = 0;
      let topRow = null;
      let bottomRow = null;

      contributions.forEach((item, index) => {
        const value = item.weighted;
        sum += value;
        if (value > max) {
          max = value;
          topRow = rows[index];
        }
        if (value < min) {
          min = value;
          bottomRow = rows[index];
        }
      });

      return {
        id: indicator.id,
        label: indicator.label,
        shortLabel: indicator.shortLabel,
        category: indicator.category,
        weight: scored[0].weight,
        spread: max - min,
        meanContribution: sum / contributions.length,
        topName: shortName(topRow?.entity?.name),
        bottomName: shortName(bottomRow?.entity?.name),
      };
    })
    .filter(Boolean)
    .sort((a, b) => b.spread - a.spread);

  if (!stats.length || stats[0].spread <= 0) return null;

  return {
    ...stats[0],
    runnerUp: stats[1] || null,
    flat: stats.filter(
      (item) => item.weight > 0 && item.spread < FLAT_SPREAD_THRESHOLD
    ),
  };
}

const MAX_NAMED_ENTITIES = 3;

/**
 * Which indicators have no data, and how much weight rides on them.
 *
 * `fullyMissing` is an indicator no entity can score, so it lowers every
 * total equally and does not change rank order.
 * `partiallyMissing` is worse for a ranking: only some entities forfeit that
 * weight, so it moves them down relative to everyone else.
 */
export function coverageSummary(rows, indicators) {
  if (!rows?.length || !indicators?.length) return null;

  const fullyMissing = [];
  const partiallyMissing = [];

  indicators.forEach((indicator) => {
    const paired = rows
      .map((row) => ({ row, item: row.contributions.find((entry) => entry.id === indicator.id) }))
      .filter((entry) => entry.item);
    if (!paired.length) return;

    const missing = paired.filter((entry) => entry.item.missing);
    if (!missing.length) return;

    const base = {
      id: indicator.id,
      label: indicator.label,
      weight: paired[0].item.weight,
    };

    if (missing.length === paired.length) {
      fullyMissing.push(base);
    } else {
      partiallyMissing.push({
        ...base,
        missingCount: missing.length,
        missingNames: missing.slice(0, MAX_NAMED_ENTITIES).map((entry) => shortName(entry.row.entity.name)),
      });
    }
  });

  if (!fullyMissing.length && !partiallyMissing.length) return null;

  return {
    fullyMissing,
    partiallyMissing,
    missingWeight: fullyMissing.reduce((sum, item) => sum + item.weight, 0),
  };
}

export function leaderSummary(rows) {
  if (!rows?.length) return null;
  const [first, second] = rows;
  return {
    name: shortName(first.entity.name),
    total: first.total,
    margin: second ? first.total - second.total : null,
    runnerUpName: second ? shortName(second.entity.name) : null,
  };
}
